import React, { forwardRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";

// Shared tour date picker for the car, bike and hotel package detail pages.
// useTourDate() owns the state (so the page's Book button can open the
// calendar and send the dates); <TourDatePicker> renders the "Tour Date" row.

const formatDisplayDate = (date) =>
  date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

// YYYY-MM-DD in local time (toISOString would shift the day for IST users).
export const formatApiDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// A saved date that is invalid or already in the past is dropped.
const readSavedTourDate = (key) => {
  const saved = sessionStorage.getItem(key);
  const date = saved ? new Date(`${saved}T00:00:00`) : null;
  return date && !isNaN(date) && date >= new Date().setHours(0, 0, 0, 0) ? date : null;
};

export const useTourDate = (days) => {
  const location = useLocation();
  // Remembered per page so the date survives the trip to /login and back.
  const storageKey = `tourDate:${location.pathname}`;
  const [tourDate, setTourDate] = useState(() => readSavedTourDate(storageKey));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setTourDate(readSavedTourDate(storageKey));
  }, [storageKey]);

  // The picker's open state is controlled here, so Escape has to be handled too.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const changeTourDate = (date) => {
    setTourDate(date);
    setOpen(false);
    if (date) sessionStorage.setItem(storageKey, formatApiDate(date));
    else sessionStorage.removeItem(storageKey);
  };

  // Last day of the trip, counting the start date as day 1.
  const tripDays = parseInt(days, 10);
  const tourEndDate =
    tourDate && tripDays > 0
      ? new Date(tourDate.getFullYear(), tourDate.getMonth(), tourDate.getDate() + tripDays - 1)
      : null;

  return { tourDate, tourEndDate, changeTourDate, open, setOpen };
};

// The whole "Tour Date" row is the date picker's trigger, so clicking the
// calendar icon, the label or the date all open the calendar.
const TourDateButton = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className="border rounded p-2 d-flex align-items-center justify-content-between w-100 bg-white"
    aria-label="Select tour date"
  >
    <FaCalendarAlt className="text-secondary me-2" size={16} />
    <span className="text-muted small me-2">Tour Date:</span>
    <span style={{ fontSize: "0.85rem" }} className={value ? "" : "text-primary"}>
      {value || "Select date"}
    </span>
  </button>
));

const TourDatePicker = ({ picker }) => (
  <div className="mb-2 w-100">
    <DatePicker
      selected={picker.tourDate}
      onChange={picker.changeTourDate}
      open={picker.open}
      onInputClick={() => picker.setOpen(true)}
      onClickOutside={() => picker.setOpen(false)}
      minDate={new Date()}
      dateFormat="dd MMM yyyy"
      customInput={<TourDateButton />}
      wrapperClassName="w-100"
      popperPlacement="bottom-end"
      // Rendered at <body> level: the package card clips anything
      // overflowing it, which cut off the calendar.
      portalId="tour-date-picker-portal"
    />
    {picker.tourEndDate ? (
      <small className="d-block text-muted text-end mt-1">
        Trip ends {formatDisplayDate(picker.tourEndDate)}
      </small>
    ) : null}
  </div>
);

export default TourDatePicker;
